import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ExternalLink } from 'lucide-react';
import { portfolioProjects } from '../../data/mock';
import { Button } from '../ui/button';

export const PortfolioPreview = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const previewProjects = portfolioProjects.slice(0, 3);

  return (
    <section ref={ref} className="relative py-24 lg:py-32">
      <div className="absolute inset-0 bg-[var(--bg-soft)]" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6"
        >
          <div>
            <span className="text-blue-400 text-sm font-semibold uppercase tracking-widest mb-3 block">
              Our Work
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-['Syne'] text-[var(--text-primary)] mb-5">
              Featured Projects
            </h2>
            <p className="text-[var(--text-muted)] text-lg max-w-xl">
              A glimpse of the digital experiences we've crafted for our clients.
            </p>
          </div>

          <Link to="/portfolio">
            <Button
              variant="outline"
              className="border-[var(--border-color)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-slate-500 hover:bg-black/5 dark:hover:bg-white/5 font-medium px-6 py-3 rounded-xl transition-all duration-300"
            >
              View All Projects
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {previewProjects.map((project, index) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.15 }}
              className="group relative rounded-2xl overflow-hidden bg-[var(--card-color)] border border-[var(--border-color)] hover:border-blue-500/20 transition-all duration-500"
            >
              <div className="relative h-56 overflow-hidden">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--card-color)] via-transparent to-transparent" />
                <div className="absolute top-4 left-4">
                  <span className="text-xs px-3 py-1.5 rounded-full bg-blue-600/80 text-white font-medium backdrop-blur-sm">
                    {project.category}
                  </span>
                </div>
                <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="w-9 h-9 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center">
                    <ExternalLink className="w-4 h-4 text-white" />
                  </div>
                </div>
              </div>

              <div className="p-6">
                <h3 className="text-lg font-bold font-['Syne'] text-[var(--text-primary)] mb-2">
                  {project.title}
                </h3>
                <p className="text-[var(--text-muted)] text-sm leading-relaxed mb-4 line-clamp-2">
                  {project.description}
                </p>
                <div className="flex flex-wrap gap-2">
                  {project.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-xs px-2.5 py-1 rounded-md bg-[var(--surface-2)] text-[var(--text-muted)] border border-[var(--border-color)]"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};